import { useState, useEffect } from 'react';
import { ChevronDown, Search, HelpCircle, ShoppingCart, CreditCard, Truck, RefreshCw, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  general: HelpCircle,
  orders: ShoppingCart,
  payments: CreditCard,
  delivery: Truck,
  subscriptions: RefreshCw,
  support: MessageSquare
};

const categoryLabels = {
  general: 'General',
  orders: 'Pedidos',
  payments: 'Pagos',
  delivery: 'Entregas',
  subscriptions: 'Suscripciones',
  support: 'Soporte'
};

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    filterFaqs();
  }, [faqs, searchQuery, selectedCategory]);

  const fetchFaqs = async () => {
    try {
      const response = await fetch(`${API}/api/faqs?audience=customer`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFaqs = () => {
    let filtered = [...faqs];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(f => f.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => 
        f.question.toLowerCase().includes(query) || 
        f.answer.toLowerCase().includes(query)
      );
    }
    
    setFilteredFaqs(filtered);
  };

  const categories = [...new Set(faqs.map(f => f.category))];

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-stone-900 mb-4">Preguntas Frecuentes</h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Encuentra respuestas a las preguntas más comunes sobre nuestros servicios
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Buscar en preguntas frecuentes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-6 text-lg rounded-full border-stone-200"
            data-testid="faq-search-input"
          />
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Todas
          </button>
          {categories.map(cat => {
            const Icon = categoryIcons[cat] || HelpCircle;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                  selectedCategory === cat
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-stone-600 hover:bg-stone-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {categoryLabels[cat] || cat}
              </button>
            );
          })}
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No se encontraron preguntas</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFaqs.map(faq => {
              const Icon = categoryIcons[faq.category] || HelpCircle;
              const isExpanded = expandedFaq === faq.faq_id;
              
              return (
                <div 
                  key={faq.faq_id}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                  data-testid={`faq-item-${faq.faq_id}`}
                >
                  <button
                    onClick={() => toggleFaq(faq.faq_id)}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-stone-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="flex-1 font-medium text-stone-900">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-14 text-stone-600 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-3xl p-8 text-center text-white">
          <MessageSquare className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">¿No encontraste lo que buscabas?</h2>
          <p className="text-emerald-100 mb-4">
            Nuestro equipo está listo para ayudarte con cualquier pregunta
          </p>
          <button className="bg-white text-emerald-700 px-6 py-3 rounded-full font-semibold hover:bg-emerald-50 transition-colors">
            Contáctanos
          </button>
        </div>
      </main>
    </div>
  );
}
